import React, { useCallback, useEffect, type FC } from 'react';
import { graphql } from 'react-relay';

import AvatarGroup, { type AvatarGroupProps } from '@atlaskit/avatar-group';

import { ConfluencePageAri } from '@atlassian/ari';

import { useSubscriptionEvent } from '@confluence/graphql/entry-points/useSubscriptionEvent';
import {
	type ShowFlagArgs,
	type FlagsStateContainer,
} from '@confluence/flags/entry-points/FlagsStateContainer';
import { useQueryInterop, useMutationInterop } from '@confluence/apollo-relay-interop';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';

import type {
	useRealtimeAccessRequestsFlagsQuery$data,
	useRealtimeAccessRequestsFlagsQuery$variables,
} from './__generated__/useRealtimeAccessRequestsFlagsQuery.graphql';
import type {
	useRealtimeAccessRequestsFlagsMutation$data,
	useRealtimeAccessRequestsFlagsMutation$variables,
} from './__generated__/useRealtimeAccessRequestsFlagsMutation.graphql';
import {
	MINUTE_LOOKBACK,
	isMoreRecentThanCachedRequestTimestamp,
	makeUpdateLastSeenRealtimeRequestCacheCallback,
} from './lastSeenRequestCachingUtils';

const REQUEST_ALREADY_PROCESSED = 'REQUEST_ALREADY_PROCESSED';
const REALTIME_ACCESS_REQUEST_FLAG_ID = 'realtime-access-request-flag-id';

type AccessRequestsList = NonNullable<
	NonNullable<
		useRealtimeAccessRequestsFlagsQuery$data['confluence_getLatestPendingRequests']
	>['latestPendingRequest']
>;

interface RealtimeAccessRequestsFlagsProps {
	contentType: string;
	contentId: string;
	onReviewRequestsClick: () => void;
	flags: FlagsStateContainer;
}

const UseRealtimeAccessRequestsQuery = graphql`
	query useRealtimeAccessRequestsFlagsQuery(
		$cloudId: ID!
		$contentId: ID!
		$duration: Int = 5
		$limit: Int = 10
		$start: String
	) {
		confluence_getLatestPendingRequests(
			cloudId: $cloudId
			contentId: $contentId
			duration: $duration
			limit: $limit
			start: $start
		) {
			latestPendingRequest {
				id
				requestAccessType
				creatorAaid
				creationDateTimestamp
				user {
					id
					displayName
					photos {
						isPrimary
						value
					}
				}
			}
		}
	}
`;

export const UseRealtimeAccessRequestsMutation = graphql`
	mutation useRealtimeAccessRequestsFlagsMutation(
		$cloudId: ID!
		$updateContentAccessRequestInput: ConfluenceUpdateContentAccessRequestInput!
	) {
		confluence_updateContentAccessRequest(
			cloudId: $cloudId
			updateContentAccessRequestInput: $updateContentAccessRequestInput
		) {
			success
			errors {
				extensions {
					errorType
					statusCode
					... on ConfluenceUpdateContentAccessRequestMutationErrorExtension {
						displayName
						processedRequestStatus
					}
				}
				message
			}
		}
	}
`;

const FlagRemovalDetector: FC<{ onDestroy: () => void; children: React.ReactNode }> = ({
	onDestroy,
	children,
}) => {
	useEffect(() => onDestroy, [onDestroy]);
	return children;
};

/**
 * Assumes requests are already filtered to only those
 * That have displayName and requestAccessType
 * */
export const makeFlagTitle = (requests: AccessRequestsList): string => {
	if (requests.length === 1) {
		const onlyRequest = requests[0];
		const name = onlyRequest?.user?.displayName!;
		const accessType = onlyRequest?.requestAccessType!;

		return `${name} wants to ${accessType?.toLowerCase()} this content`;
	}

	if (requests.length > 1) {
		const firstName = requests[0]?.user?.displayName!;

		if (requests.length === 2) {
			const secondName = requests[1]?.user?.displayName!;
			return `${firstName} and ${secondName} want to access your content`;
		}

		const remainingCount = requests.length - 1;
		return `${firstName} and ${remainingCount} others want to access your content`;
	}

	// Should not happen, because method gets invoked if there's at least one request
	return '';
};

export const makeFlagDescription = (requests: AccessRequestsList): string | null => {
	if (requests.length === 1) {
		// ToDo: This becomes relevant for blocked access
		// Make blocked access API call
		// Return appropriate string description
	}

	return null;
};

export const makeFlagCustomIcon = (requests: AccessRequestsList) => {
	const avatarData: AvatarGroupProps['data'] = requests
		.filter((request) => request?.user?.displayName && request?.requestAccessType)
		.map((request) => ({
			name: request?.user?.displayName!,
			src: request?.user?.photos?.find((photo) => photo?.isPrimary)?.value ?? undefined,
			key: request?.user?.id!,
		}));

	const maxCount = avatarData.length === 1 ? 1 : 2;
	return <AvatarGroup data={avatarData} maxCount={maxCount} />;
};

export const makeFlagActions = (
	requests: AccessRequestsList,
	processRequest: (request: AccessRequestsList[number], decision: 'APPROVE' | 'DENY') => void,
	onReview: () => void,
): ShowFlagArgs['actions'] => {
	if (requests.length === 1) {
		return [
			{ content: 'Approve', onClick: () => processRequest(requests[0], 'APPROVE') },
			{ content: 'Deny', onClick: () => processRequest(requests[0], 'DENY') },
		];
	}

	if (requests.length > 1) {
		return [{ content: 'Review requests', onClick: onReview }];
	}

	return [];
};

// Called in response to a ping that there is a real-time access request
export const useRealtimeAccessRequestsFlags = ({
	contentId,
	contentType,
	flags,
	onReviewRequestsClick: onReviewRequestsClickCallback,
}: RealtimeAccessRequestsFlagsProps) => {
	const { cloudId } = useSessionData();

	const [mutateRequest] = useMutationInterop<
		useRealtimeAccessRequestsFlagsMutation$data,
		useRealtimeAccessRequestsFlagsMutation$variables
	>(UseRealtimeAccessRequestsMutation);

	const onProcessRequestClick = useCallback(
		async (request: AccessRequestsList[number], decision: 'APPROVE' | 'DENY') => {
			// Optimistically hide flag to make things look speedy
			void flags.hideFlag(REALTIME_ACCESS_REQUEST_FLAG_ID);

			const result = await mutateRequest({
				variables: {
					cloudId,
					updateContentAccessRequestInput: {
						contentId,
						requestId: request?.id!,
						accessType: request?.requestAccessType!,
						accessRequestedAccountId: request?.user?.id!,
						approvalDecision: decision,
					},
				},
			});

			const successfulMutation = result.data?.confluence_updateContentAccessRequest?.success;
			const requestAlreadyProcessed =
				result?.data?.confluence_updateContentAccessRequest?.errors?.find(
					(err) => err?.message === REQUEST_ALREADY_PROCESSED,
				);

			if (successfulMutation) {
				void flags.showSuccessFlag({
					id: `${REALTIME_ACCESS_REQUEST_FLAG_ID}_SUCCESS`,
					title: `Request ${decision === 'APPROVE' ? 'approved' : 'denied'}`,
					description:
						decision === 'APPROVE'
							? `${request?.user?.displayName} can now successfully ${request?.requestAccessType?.toLowerCase()} this content`
							: `${request?.user?.displayName}'s access to this content won't change`,
					isAutoDismiss: true,
				});
			} else if (requestAlreadyProcessed) {
				void flags.showInfoFlag({
					id: `${REALTIME_ACCESS_REQUEST_FLAG_ID}_INFO`,
					title: 'Someone already responded to this this request',
					isAutoDismiss: true,
				});
			} else {
				void flags.showErrorFlag({
					id: `${REALTIME_ACCESS_REQUEST_FLAG_ID}_ERROR`,
					title: 'We ran into a problem resolving the access request. Try again later.',
					isAutoDismiss: true,
				});
			}
		},
		[cloudId, contentId, mutateRequest, flags],
	);

	const onReviewRequestsClick = useCallback(() => {
		onReviewRequestsClickCallback();
		void flags.hideFlag(REALTIME_ACCESS_REQUEST_FLAG_ID);
	}, [onReviewRequestsClickCallback, flags]);

	const maybeShowRealtimeFlag = useCallback(
		async (data: useRealtimeAccessRequestsFlagsQuery$data) => {
			// These requests are already less than 5 minues old straight from the API
			const accessRequests = data.confluence_getLatestPendingRequests?.latestPendingRequest ?? [];

			// We just need to remove requests older than the "last seen" one in the cache
			// Also ensure all requests have bare minimum info to display flag correctly
			const filteredRequests = accessRequests.filter((request) => {
				const hasAllNecessaryData = request?.user?.displayName && request?.requestAccessType;
				const requestCreationTimestamp = Number(request?.creationDateTimestamp);
				const isMoreRecentThanLastSeenRequest = isMoreRecentThanCachedRequestTimestamp(
					contentId,
					requestCreationTimestamp,
				);

				return hasAllNecessaryData && isMoreRecentThanLastSeenRequest;
			});

			const latestIncomingRequestCreatedAt = Number(filteredRequests[0]?.creationDateTimestamp);
			const cacheLatestRequest = makeUpdateLastSeenRealtimeRequestCacheCallback(
				contentId,
				latestIncomingRequestCreatedAt,
			);

			if (filteredRequests.length === 0) {
				return;
			}

			// Only hiding any currently-displayed flag if there's another flag to show (filteredRequests > 0)
			// Need the await to ensure proper closing animation of currently-displayed realtime flag
			await flags.hideFlag(REALTIME_ACCESS_REQUEST_FLAG_ID);

			await flags.showCustomFlag({
				id: REALTIME_ACCESS_REQUEST_FLAG_ID,
				isAutoDismiss: true,
				autoDismissSeconds: 16,
				customIcon: makeFlagCustomIcon(filteredRequests),
				title: (
					<FlagRemovalDetector onDestroy={cacheLatestRequest}>
						{makeFlagTitle(filteredRequests)}
					</FlagRemovalDetector>
				),
				description: makeFlagDescription(filteredRequests),
				actions: makeFlagActions(filteredRequests, onProcessRequestClick, onReviewRequestsClick),
			});
		},
		[contentId, flags, onProcessRequestClick, onReviewRequestsClick],
	);

	const { refetch } = useQueryInterop<
		useRealtimeAccessRequestsFlagsQuery$data,
		useRealtimeAccessRequestsFlagsQuery$variables
	>(UseRealtimeAccessRequestsQuery, {
		notifyOnNetworkStatusChange: true,
		fetchPolicy: 'network-only',
		variables: {
			cloudId,
			contentId,
			duration: MINUTE_LOOKBACK,
		},
		onCompleted: maybeShowRealtimeFlag,
	});

	useSubscriptionEvent({
		contentId,
		contentType,
		delta: 'contentAccessRequested',
		subscriberId: ConfluencePageAri.create({
			siteId: cloudId,
			pageId: contentId,
		}).toString(),
		onDataReceived: refetch,
		/** TODO: Also monitor for tab inactivity and disable the subscription */
		skip: false,
	});
};
